import { useEffect, useState } from "react";
import { CreditCardIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ITillConfig } from "../../models/till/ITillConfig";
import { ITillSale } from "../../models/till/ITillSale";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { needsOperator, shouldPollSale } from "../../utils/till/paymentStatus";
import { isCashEnabled, validateCardTender } from "../../utils/till/tenderValidation";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import TillReaderSelect from "./TillReaderSelect";

interface CardPaymentModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  total: number;
  config: ITillConfig | undefined;
  sale: ITillSale | undefined;
  readerId: string | null;
  onReaderChange: (readerId: string) => void;
  onStart: () => void;
  onRetry: () => void;
  onCancel: () => void;
  onSwitchToCash: () => void;
  isStarting: boolean;
  isRetrying: boolean;
  isCancelling: boolean;
  error?: string | null;
}

/**
 * Drives a card payment on a Stripe reader: choose the reader, send the sale, watch the reader,
 * and deal with a decline. The parent polls the sale; this only renders what it says.
 */
export default function CardPaymentModal({
  open, setOpen, total, config, sale, readerId, onReaderChange, onStart, onRetry, onCancel, onSwitchToCash,
  isStarting, isRetrying, isCancelling, error,
}: CardPaymentModalProps) {
  const status = sale?.payment.status;
  const waiting = shouldPollSale(status);
  const declined = needsOperator(status);
  const readerCheck = validateCardTender(config, readerId);
  const cashEnabled = isCashEnabled(config);
  const [waitedLong, setWaitedLong] = useState(false);

  useEffect(() => {
    if (!waiting) {
      setWaitedLong(false);
      return;
    }
    const t = window.setTimeout(() => setWaitedLong(true), 90_000);
    return () => window.clearTimeout(t);
  }, [waiting, sale?.payment.attempts]);

  // While the reader has the card the modal can only be left via Cancel.
  const guardedSetOpen: React.Dispatch<React.SetStateAction<boolean>> = (v) => {
    if (waiting || isStarting || isCancelling) return;
    setOpen(v);
  };

  const readerLabel = config?.readers.find((r) => r.id === (sale?.payment.readerId ?? readerId))?.label;

  return (
    <Modal open={open} setOpen={guardedSetOpen} width="max-w-lg">
      <div className="p-5 text-left">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <CreditCardIcon className="h-7 w-7" />
          </span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Card payment</h3>
            <p className="text-sm text-gray-500">Total to pay <span className="font-semibold text-gray-900">{PriceFormat(total)}</span></p>
          </div>
        </div>

        {!sale || status === "Cancelled" || (status === "Failed" && !sale.payment.paymentIntentId) ? (
          <>
            <p className="mb-2 text-sm font-medium text-gray-700">Choose a reader</p>
            <TillReaderSelect readers={config?.readers ?? []} value={readerId} onChange={onReaderChange} disabled={isStarting} />
            {!config?.stripeEnabled || config.stripeMessage ? (
              // stripeMessage also carries "could not reach Stripe" when the reader list failed, which
              // otherwise looks identical to having no readers registered.
              <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">{config?.stripeMessage ?? "Card payments are not set up for this account."}</p>
            ) : null}
            {error ? <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
            <div className="mt-4 flex justify-between gap-3">
              <ButtonInput label="Back" size="lg" classes="!bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50" onClick={() => setOpen(false)} disabled={isStarting} />
              <ButtonInput label="Send to reader" size="lg" classes="flex-1" onClick={onStart} disabled={!readerCheck.ok} isLoading={isStarting} />
            </div>
          </>
        ) : waiting ? (
          <>
            <div className="flex flex-col items-center py-6 text-center">
              <LoadingWheel wrapperClassName="mb-4" />
              <p className="text-lg font-medium text-gray-900">Follow the instructions on the reader</p>
              <p className="text-sm text-gray-500">{readerLabel ? `Waiting for ${readerLabel}.` : "Waiting for the card reader."} Ask the customer to tap or insert their card.</p>
              {waitedLong ? <p className="mt-3 text-sm text-amber-700">Still waiting. If the reader shows an error, cancel here and try again.</p> : null}
            </div>
            <div className="flex justify-end">
              <ButtonInput label="Cancel payment" size="lg" classes="!bg-white !text-red-700 border !border-red-300 hover:!bg-red-50" onClick={onCancel} isLoading={isCancelling} />
            </div>
          </>
        ) : declined || status === "Pending" ? (
          <>
            <div className="flex items-start gap-3 rounded-md bg-red-50 p-4">
              <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-red-600" />
              <div>
                <p className="font-medium text-red-800">{status === "Pending" ? "The reader didn't take the payment" : "Card declined"}</p>
                <p className="text-sm text-red-700">{sale.payment.failureReason ?? "The payment was not approved. Try again or take another payment method."}</p>
              </div>
            </div>
            {error ? <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
            <div className={classNames("mt-4 grid grid-cols-1 gap-2", cashEnabled ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
              <ButtonInput label="Try again" size="lg" classes="" onClick={onRetry} isLoading={isRetrying} disabled={isCancelling} />
              {cashEnabled ? (
                <ButtonInput label="Pay by cash instead" size="lg" classes="!bg-emerald-600 hover:!bg-emerald-700" onClick={onSwitchToCash} disabled={isRetrying || isCancelling} />
              ) : null}
              <ButtonInput label="Cancel sale" size="lg" classes="!bg-white !text-red-700 border !border-red-300 hover:!bg-red-50" onClick={onCancel} isLoading={isCancelling} disabled={isRetrying} />
            </div>
          </>
        ) : status === "Failed" ? (
          <>
            <div className="flex items-start gap-3 rounded-md bg-red-50 p-4">
              <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-red-600" />
              <div>
                <p className="font-medium text-red-800">The sale could not be completed</p>
                <p className="text-sm text-red-700">{sale.payment.failureReason ?? "Something went wrong."}</p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <ButtonInput label="Close" size="lg" classes="" onClick={() => setOpen(false)} />
            </div>
          </>
        ) : (
          <div className="py-6 text-center text-gray-600">Payment approved.</div>
        )}
      </div>
    </Modal>
  );
}
